import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import type { Key } from '@litellm-ui/shared';

export type { Key };

export function useKeys(teamId?: string) {
  return useQuery({
    queryKey: ['keys', teamId],
    queryFn: () => api<Key[]>('/api/keys', { query: { team_id: teamId } }),
  });
}

export function useKeyInfo(key: string | undefined) {
  return useQuery({
    queryKey: ['key-info', key],
    queryFn: () =>
      api<Key>('/api/keys/info', {
        query: { key },
      }),
    enabled: !!key,
  });
}

export function useDeleteKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (key: string) =>
      api('/api/keys/delete', { method: 'POST', body: { key } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['keys'] }),
  });
}

export function useBlockKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ key, blocked }: { key: string; blocked: boolean }) =>
      api('/api/keys/block', { method: 'POST', body: { key, blocked } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['keys'] }),
  });
}

export function useGenerateKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<Key> & { models?: string[] }) =>
      api<Key>('/api/keys/generate', { method: 'POST', body: data }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['keys'] }),
  });
}

export function useUpdateKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<Key> & { key: string }) =>
      api('/api/keys/update', { method: 'POST', body: data }),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['keys'] });
      qc.invalidateQueries({ queryKey: ['key-info', vars.key] });
    },
  });
}

export function useRegenerateKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (key: string) =>
      api<Key>('/api/keys/regenerate', { method: 'POST', body: { key } }),
    onSuccess: (_data, key) => {
      qc.invalidateQueries({ queryKey: ['keys'] });
      qc.invalidateQueries({ queryKey: ['key-info', key] });
    },
  });
}

export function useResetKeySpend() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ key, reset_to = 0 }: { key: string; reset_to?: number }) =>
      api('/api/keys/reset-spend', {
        method: 'POST',
        body: { key, reset_to },
      }),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['keys'] });
      qc.invalidateQueries({ queryKey: ['key-info', vars.key] });
    },
  });
}